// Generated using Magic MCP Server for production-ready UI components

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Sliders, 
  Play, 
  Pause, 
  RotateCw, 
  Download,
  Maximize2,
  Settings,
  Info
} from 'lucide-react'
import { useSimulationStore } from '../store/simulationStore'

export const ControlPanel: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [activeTab, setActiveTab] = useState<'physics' | 'visual' | 'presets'>('physics')
  
  const {
    isPlaying,
    particleCount,
    gravityStrength,
    particleSize,
    particleColor,
    simulationSpeed,
    togglePlayPause,
    resetSimulation,
    updateSettings,
    exportConfiguration
  } = useSimulationStore()

  const SliderControl = ({ 
    label, 
    value, 
    onChange, 
    min, 
    max, 
    step = 1,
    unit = ''
  }: {
    label: string
    value: number
    onChange: (value: number) => void
    min: number
    max: number
    step?: number
    unit?: string
  }) => (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-300">{label}</span>
        <span className="text-blue-400 font-mono">
          {value.toFixed(step < 1 ? 2 : 0)}{unit}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer 
                     slider-thumb:appearance-none slider-thumb:w-4 slider-thumb:h-4 
                     slider-thumb:bg-blue-500 slider-thumb:rounded-full slider-thumb:cursor-pointer
                     hover:slider-thumb:bg-blue-400 transition-all"
        />
        <div 
          className="absolute top-0 left-0 h-2 bg-blue-500 rounded-lg pointer-events-none"
          style={{ width: `${((value - min) / (max - min)) * 100}%` }}
        />
      </div>
    </div>
  )

  return (
    <motion.div
      initial={{ x: -300 }}
      animate={{ x: isExpanded ? 0 : -280 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed left-0 top-1/2 -translate-y-1/2 z-50 w-80"
      data-testid="control-panel"
    >
      <div className="bg-gray-900/95 backdrop-blur-lg rounded-r-2xl shadow-2xl border border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Sliders className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-semibold">Simulation Controls</h2>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-800">
          {(['physics', 'visual', 'presets'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors
                ${activeTab === tab 
                  ? 'text-blue-400 border-b-2 border-blue-400' 
                  : 'text-gray-400 hover:text-white'}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="p-4 space-y-6"
          >
            {activeTab === 'physics' && (
              <>
                <SliderControl
                  label="Particle Count"
                  value={particleCount}
                  onChange={(v) => updateSettings({ particleCount: v })}
                  min={1000}
                  max={50000}
                  step={1000}
                />
                <SliderControl
                  label="Gravity Strength"
                  value={gravityStrength}
                  onChange={(v) => updateSettings({ gravityStrength: v })}
                  min={0.1}
                  max={5}
                  step={0.1}
                />
                <SliderControl
                  label="Simulation Speed"
                  value={simulationSpeed}
                  onChange={(v) => updateSettings({ simulationSpeed: v })}
                  min={0.1}
                  max={3}
                  step={0.1}
                  unit="x"
                />
              </>
            )}

            {activeTab === 'visual' && (
              <>
                <SliderControl
                  label="Particle Size"
                  value={particleSize}
                  onChange={(v) => updateSettings({ particleSize: v })}
                  min={0.1}
                  max={2}
                  step={0.1}
                />
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Particle Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={particleColor}
                      onChange={(e) => updateSettings({ particleColor: e.target.value })}
                      className="w-full h-10 bg-gray-800 rounded cursor-pointer"
                    />
                    <button className="px-3 py-2 bg-gray-800 text-gray-400 rounded hover:bg-gray-700">
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'presets' && (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'Spiral Galaxy', particles: 15000, gravity: 1.2 },
                  { name: 'Nebula', particles: 25000, gravity: 0.5 },
                  { name: 'Star Cluster', particles: 10000, gravity: 2.5 },
                  { name: 'Cosmic Web', particles: 30000, gravity: 0.8 }
                ].map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => updateSettings({ 
                      particleCount: preset.particles, 
                      gravityStrength: preset.gravity 
                    })}
                    className="p-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors
                             text-left space-y-1 group"
                  >
                    <div className="text-sm font-medium text-white group-hover:text-blue-400">
                      {preset.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {preset.particles.toLocaleString()} particles
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="p-4 border-t border-gray-800 flex gap-2">
          <button
            onClick={togglePlayPause}
            className="flex-1 py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg
                     font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="play-pause-button"
            aria-label={isPlaying ? 'Pause simulation' : 'Play simulation'}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <button
            onClick={resetSimulation}
            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white 
                     rounded-lg transition-colors"
            title="Reset"
          >
            <RotateCw className="w-5 h-5" />
          </button>
          <button
            onClick={exportConfiguration}
            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white 
                     rounded-lg transition-colors"
            title="Export Config"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Expand/Collapse Tab */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute -right-12 top-1/2 -translate-y-1/2 bg-gray-900/95 
                   backdrop-blur-lg rounded-r-lg p-3 border border-l-0 border-gray-800"
      >
        <Settings className={`w-5 h-5 text-gray-400 transition-transform ${
          isExpanded ? 'rotate-180' : ''
        }`} />
      </button>
    </motion.div>
  )
}